/**
 * Shared pop animation for portal dropdowns anchored to a trigger (ActionMenu,
 * UserAccountMenu). `originX` + `droppedUp` pick the transform-origin corner
 * nearest the trigger; keyframes travel toward/away from that corner so a
 * bottom-row flip-up still reads as "growing out of the dots".
 */

export const ANCHORED_MENU_ENTER_MS = 200;
export const ANCHORED_MENU_EXIT_MS = 180;

export function getAnchoredMenuTransformOrigin(originX, droppedUp) {
  return `${originX}px ${droppedUp ? '100%' : '0'}`;
}

export function getAnchoredMenuPanelAnimation(droppedUp, isExiting, exitMs = ANCHORED_MENU_EXIT_MS) {
  if (isExiting) {
    const key = droppedUp ? 'crewvex-action-menu-pop-out-up' : 'crewvex-action-menu-pop-out-down';
    return `${key} ${exitMs}ms cubic-bezier(0.4, 0, 1, 1) forwards`;
  }
  const key = droppedUp ? 'crewvex-action-menu-pop-up' : 'crewvex-action-menu-pop-down';
  return `${key} ${ANCHORED_MENU_ENTER_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
}

/**
 * The ACCOUNT menu grows out of the avatar (founder, 2026-10-24, KAN-987; replaces
 * KAN-975's 97% pop): the card scales 30% → 100% from the avatar's CENTRE (both axes,
 * see getAccountMenuTransformOrigin), 220ms on a decelerate with no overshoot, and the
 * avatar presses 1 → 0.9 → 1 as it opens. It closes back into the avatar in 150ms.
 * Rows arrive WITH the card (no row fade). Account menu only: ActionMenu keeps the
 * pop above. Reduced motion = a plain short fade, no avatar press.
 */
export const ACCOUNT_MENU_ENTER_MS = 220;
export const ACCOUNT_MENU_EXIT_MS = 150;
/** The menu's dim fades in on the same short beat as the card. */
export const ACCOUNT_MENU_DIM_IN_MS = 120;
export const ACCOUNT_AVATAR_PRESS_MS = 160;
export const ACCOUNT_AVATAR_PRESS_ANIMATION = `crewvex-account-avatar-press ${ACCOUNT_AVATAR_PRESS_MS}ms ease-out`;

/** Avatar centre in panel coordinates (originY may sit outside the box: that is intended). */
export function getAccountMenuTransformOrigin(originX, originY, droppedUp = false) {
  if (typeof originY !== 'number' || Number.isNaN(originY)) {
    return getAnchoredMenuTransformOrigin(originX, droppedUp);
  }
  return `${originX}px ${originY}px`;
}

export function getAccountMenuPanelAnimation(droppedUp, isExiting, reduced = false) {
  if (reduced) {
    return isExiting
      ? 'crewvex-action-menu-scrim-out 120ms ease forwards'
      : 'crewvex-action-menu-scrim-in 120ms ease';
  }
  if (isExiting) {
    const key = droppedUp ? 'crewvex-account-menu-out-up' : 'crewvex-account-menu-out-down';
    return `${key} ${ACCOUNT_MENU_EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1) forwards`;
  }
  const key = droppedUp ? 'crewvex-account-menu-in-up' : 'crewvex-account-menu-in-down';
  return `${key} ${ACCOUNT_MENU_ENTER_MS}ms cubic-bezier(0.2, 0.9, 0.25, 1)`;
}

export const ANCHORED_MENU_KEYFRAMES_CSS = `
  /* KAN-989: every account-menu row icon draws at ONE 2px line, whatever weight its shell passed. */
  [data-cvx-account-menu-icon] svg { stroke-width: 2; width: var(--cvx-acct-icon, 18px); height: var(--cvx-acct-icon, 18px); }
  @keyframes crewvex-account-menu-in-down {
    0%   { opacity: 0; transform: scale(0.3); }
    30%  { opacity: 1; }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes crewvex-account-menu-in-up {
    0%   { opacity: 0; transform: scale(0.3); }
    30%  { opacity: 1; }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes crewvex-account-menu-out-down {
    from { opacity: 1; transform: scale(1); }
    to   { opacity: 0; transform: scale(0.4); }
  }
  @keyframes crewvex-account-menu-out-up {
    from { opacity: 1; transform: scale(1); }
    to   { opacity: 0; transform: scale(0.4); }
  }
  @keyframes crewvex-account-avatar-press {
    0%   { transform: scale(1); }
    45%  { transform: scale(0.9); }
    100% { transform: scale(1); }
  }
  @keyframes crewvex-action-menu-pop-down {
    from { opacity: 0; transform: translateY(-8px) scale(0.55); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes crewvex-action-menu-pop-up {
    from { opacity: 0; transform: translateY(8px) scale(0.55); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes crewvex-action-menu-pop-out-down {
    from { opacity: 1; transform: translateY(0) scale(1); }
    to   { opacity: 0; transform: translateY(-6px) scale(0.65); }
  }
  @keyframes crewvex-action-menu-pop-out-up {
    from { opacity: 1; transform: translateY(0) scale(1); }
    to   { opacity: 0; transform: translateY(6px) scale(0.65); }
  }
  @keyframes crewvex-action-menu-scrim-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes crewvex-action-menu-scrim-out {
    from { opacity: 1; }
    to   { opacity: 0; }
  }
`;
